import { ChunkLoadErrorHandler } from './core/chunk-load-error.handler';
import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';

import { provideEchartsCore } from 'ngx-echarts';

import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from './core/services/custom-paginator-intl.service';
import * as Sentry from "@sentry/angular";
import { environment } from '../environments/environment';
import { ChunkReloadService } from './core/services/chunk-reload.service';

// ... existing imports ...

// PLU-266: replayIntegration() is the suspected cause of the freeze when
// editing a class/CSS property in DevTools' Elements/Styles panel — rrweb's
// MutationObserver-based recording reacts to every DOM/style mutation
// DevTools makes, and zone.js runs those callbacks (and the change detection
// they trigger) inside Angular's zone. When sentryReplayEnabled is false,
// replayIntegration() is never constructed or added — not merely sampled at
// 0 — so no MutationObserver from it ever attaches. Error reporting and
// tracing (browserTracingIntegration) are unaffected either way.
const sentryIntegrations = [Sentry.browserTracingIntegration()];
if (environment.sentryReplayEnabled) {
  sentryIntegrations.push(Sentry.replayIntegration());
}

// Initialize Sentry before the app config is exported
Sentry.init({
  dsn: environment.sentryDsn,
  environment: environment.sentryEnvironment || (environment.production ? 'production' : 'development'),
  release: environment.sentryRelease || 'vlugbezorgd-admin@18.0.0',
  integrations: sentryIntegrations,
  // Tracing
  tracesSampleRate: environment.sentryTracesSampleRate,
  // apiBaseUrl is environment-specific and can be overridden by CI/CD.
  tracePropagationTargets: environment.production
    ? [environment.apiBaseUrl]
    : ['localhost', environment.apiBaseUrl],
  // Session Replay — only meaningful alongside replayIntegration() above.
  ...(environment.sentryReplayEnabled ? {
    replaysSessionSampleRate: environment.sentryReplaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentryReplaysOnErrorSampleRate,
  } : {}),
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideAnimations(),
    importProvidersFrom(
      TranslateModule.forRoot({

      })
    ),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      // Cache-bust per session so stale browser-cached translations
      // (page-header keys, calendar months, etc.) get refreshed on each
      // app load. Same timestamp for the session = within-session cache is fine.
      suffix: `.json?v=${Date.now()}`
    }),
    // Lazy load ECharts - saves ~500KB from initial bundle
    provideEchartsCore({
      echarts: () => import('echarts')
    }),
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl,
    },
    // Sentry Providers
    {
      // Chunk-load recovery in front of Sentry: a stale-chunk failure after a
      // deploy reloads the tab once; everything else is delegated to Sentry's
      // handler untouched. Clearing the guard on a clean boot re-arms it.
      provide: ErrorHandler,
      useFactory: () => {
        ChunkLoadErrorHandler.clearReloadGuard();
        return new ChunkLoadErrorHandler(
          Sentry.createErrorHandler({ showDialog: false }),
        );
      },
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    // Guarded reload on lazy-chunk NavigationError (PLU-121)
    {
      provide: APP_INITIALIZER,
      useFactory: (chunkReloadService: ChunkReloadService) => () => { },
      deps: [ChunkReloadService],
      multi: true,
    },
  ]
};
